import { useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useState } from "react";
import { createApp } from "../../lib/pixi/createApp";
import { BoardScene } from "../../lib/pixi/BoardScene";
import type { PremiumSquare, PlacedTile, WordGrid, Tile, GridHint } from "../../../shared/types";
import { DEBUG } from "../../../shared/constants";

export interface GameBoardHandle {
  revealTiles: (solution: Record<string, string>) => void;
  spawnVictoryConfetti: () => void;
  floatScore: (x: number, y: number, amount: number) => void;
  pulseTile: (x: number, y: number) => void;
  clearParticles: () => void;
  highlightClue: (clue: GridHint) => void;
  clearClueHighlight: () => void;
}

interface Props {
  premiumSquares: PremiumSquare[];
  board: PlacedTile[];
  puzzle?: WordGrid | null;
  opponentSolvedKeys?: Set<string>;
  onCellClick?: (x: number, y: number) => void;
  onTileClick?: (tileId: string, letter: string, x: number, y: number) => void;
  onDropTile?: (tile: Tile, x: number, y: number) => void;
  animateNewTiles?: boolean;
  touchDragRef?: React.MutableRefObject<{
    tile: Tile;
    sideIndex: number;
    index: number;
    ghost: HTMLElement | null;
  } | null>;
}

const GameBoard = forwardRef<GameBoardHandle, Props>(
  ({ premiumSquares, board, puzzle, opponentSolvedKeys, onCellClick, onTileClick, onDropTile, animateNewTiles, touchDragRef }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<BoardScene | null>(null);
    const appRef = useRef<any>(null);
    const prevBoardRef = useRef<PlacedTile[]>([]);
    const puzzleSetRef = useRef(false);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const [isReady, setIsReady] = useState(false);

    const updateScale = useCallback(() => {
      if (!sceneRef.current || !containerRef.current || !appRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      // Resize the PixiJS renderer to match container and restore CSS fill
      appRef.current.renderer.resize(w, h);
      appRef.current.canvas.style.width = "100%";
      appRef.current.canvas.style.height = "100%";
      sceneRef.current.setScale(w, h);
    }, []);

    useImperativeHandle(ref, () => ({
      revealTiles: (solution: Record<string, string>) => {
        sceneRef.current?.revealTiles(solution, 80);
      },
      spawnVictoryConfetti: () => {
        sceneRef.current?.spawnVictoryConfetti();
      },
      floatScore: (x: number, y: number, amount: number) => {
        sceneRef.current?.floatScore(x, y, amount);
      },
      pulseTile: (x: number, y: number) => {
        sceneRef.current?.pulseTile(x, y);
      },
      clearParticles: () => {
        sceneRef.current?.clearParticles();
      },
      highlightClue: (clue: GridHint) => {
        sceneRef.current?.highlightClue(clue);
      },
      clearClueHighlight: () => {
        sceneRef.current?.clearClueHighlight();
      },
    }));

    useEffect(() => {
      if (!containerRef.current || appRef.current) return;
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      let disposed = false;

      createApp(container, w, h).then((app) => {
        // StrictMode (dev) can start two createApp calls for the same
        // container; drop any app that resolves after this effect was cleaned up.
        if (disposed) {
          try {
            app.ticker.stop();
            app.destroy(true);
          } catch {
            // best-effort teardown of a stale app
          }
          return;
        }
        appRef.current = app;
        const scene = new BoardScene();
        scene.onCellClick = onCellClick;
        scene.onTileClick = onTileClick;
        app.stage.addChild(scene);
        sceneRef.current = scene;
        updateScale();
        setIsReady(true);
      });

      // Handle resize
      resizeObserverRef.current = new ResizeObserver(() => {
        updateScale();
      });
      resizeObserverRef.current.observe(container);

      // Temporarily disable canvas pointer events during HTML5 drag so that
      // the container div receives dragover/drop events for rack-to-board drops.
      const handleWindowDragStart = () => {
        const canvas = container.querySelector("canvas");
        if (canvas) {
          (canvas as HTMLCanvasElement).style.pointerEvents = "none";
        }
      };
      const handleWindowDragEnd = () => {
        const canvas = container.querySelector("canvas");
        if (canvas) {
          (canvas as HTMLCanvasElement).style.pointerEvents = "auto";
        }
      };
      window.addEventListener("dragstart", handleWindowDragStart);
      window.addEventListener("dragend", handleWindowDragEnd);

      return () => {
        disposed = true;
        resizeObserverRef.current?.disconnect();
        window.removeEventListener("dragstart", handleWindowDragStart);
        window.removeEventListener("dragend", handleWindowDragEnd);
        if (appRef.current) {
          // Stopping the ticker first prevents an in-flight render frame from
          // racing the texture teardown (which throws inside Pixi and would
          // abort the React commit when navigating away, blanking the page).
          try {
            appRef.current.ticker.stop();
            appRef.current.destroy(true);
          } catch {
            // Best-effort teardown: the app is leaving with the page.
          }
          appRef.current = null;
        }
        sceneRef.current = null;
      };
    }, []);

    useEffect(() => {
      if (!sceneRef.current || !isReady) return;
      sceneRef.current.onCellClick = onCellClick;
      sceneRef.current.onTileClick = onTileClick;
      if (puzzle && !puzzleSetRef.current) {
        sceneRef.current.setPuzzle(puzzle);
        puzzleSetRef.current = true;
        if (DEBUG && puzzle.solution) {
          sceneRef.current.showDebugSolution(puzzle.solution);
        }
      }
      sceneRef.current.applyPremiumSquares(premiumSquares);
      if (opponentSolvedKeys) {
        sceneRef.current.setOpponentSolvedKeys(opponentSolvedKeys);
      }
      const hasNewTiles = board.length > prevBoardRef.current.length;
      sceneRef.current.updateBoard(board, animateNewTiles && hasNewTiles);
      prevBoardRef.current = [...board];
      updateScale();
    }, [isReady, premiumSquares, board, puzzle, opponentSolvedKeys, onCellClick, onTileClick, animateNewTiles, updateScale]);

    // Touch drop support: listen for touchend on the document and check if
    // the finger was lifted over this GameBoard.
    useEffect(() => {
      if (!touchDragRef) return;

      const onTouchEnd = (e: TouchEvent) => {
        if (!touchDragRef.current || !onDropTile || !containerRef.current || !sceneRef.current) return;

        const touch = e.changedTouches[0];
        if (!touch) return;

        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!target || !containerRef.current.contains(target as Node)) return;

        const rect = containerRef.current.getBoundingClientRect();
        const gridPos = sceneRef.current.screenToGrid(
          touch.clientX - rect.left,
          touch.clientY - rect.top
        );
        if (gridPos) {
          onDropTile(touchDragRef.current.tile, gridPos.x, gridPos.y);
          touchDragRef.current = null;
        }
      };

      document.addEventListener("touchend", onTouchEnd);
      return () => {
        document.removeEventListener("touchend", onTouchEnd);
      };
    }, [onDropTile, touchDragRef]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      if (!onDropTile || !containerRef.current || !sceneRef.current) return;
      // Get the dragged tile data from the dataTransfer
      const tileData = e.dataTransfer.getData("application/json");
      if (!tileData) return;
      const parsed = JSON.parse(tileData) as { sideIndex: number; tileIndex: number; tile: Tile };
      const tile = parsed.tile;
      const rect = containerRef.current.getBoundingClientRect();
      const gridPos = sceneRef.current.screenToGrid(
        e.clientX - rect.left,
        e.clientY - rect.top
      );
      if (gridPos) {
        onDropTile(tile, gridPos.x, gridPos.y);
      }
    }, [onDropTile]);

    return (
      <div
        ref={containerRef}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="w-full h-full min-h-[300px] border-2 border-board-border rounded-lg overflow-hidden"
      />
    );
  }
);

GameBoard.displayName = "GameBoard";
export default GameBoard;
